import React, { useState } from 'react';

import { MessageTemplateEditor } from '../../message-templates/components/message-template-editor';
import { TemplateList } from '../../message-templates/components/template-list';
import { ApplicantMessageTemplateParser } from '../../message-templates/parsers/applicant-message-template-parser';
import { EmployeeMessageTemplateParser } from '../../message-templates/parsers/employee-message-template-parser';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';

import { getTemplateId } from '../../message-templates/utils/template-utils';
import { CkEditorNewlineTrimmer } from '../../util/ckeditor-newline-trimmer';
import { MESSAGE_TYPE } from '../../util/message-types';

type InputProps = {
  messageType: string | null;
  templatesForType: Array<Template>;
  parser: EmployeeMessageTemplateParser | ApplicantMessageTemplateParser;
  shouldParseTemplate: boolean;

  // Template crud actions
  updateTemplate: (update: Record<string, any>) => Promise<void>;
  createTemplate: (create: Record<string, any>) => Promise<void>;
  deleteTemplate: (templateId: number) => Promise<void>;

  // Dialog controls
  templateType: 'MESSAGE' | 'SURVEY';
  insertTemplate: (selectedSavedMessage: string) => void;
  onClose: () => void;
};

export const MessageTemplatesDialog = ({
  templateType,
  messageType,
  templatesForType,
  parser,
  shouldParseTemplate,
  updateTemplate,
  createTemplate,
  deleteTemplate,
  insertTemplate,
  onClose,
}: InputProps) => {
  const [isAddingNewTemplate, setIsAddingNewTemplate] = useState<boolean>(false);
  const [isEditingId, setIsEditingId] = useState<number | null>(null);
  const [templateString, setTemplateString] = useState<string>('');

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [preventSaving, setPreventSaving] = useState<boolean>(false);

  const editingTemplate = templatesForType.find((t: Template) => getTemplateId(t) === isEditingId);
  const selectedTemplate = templatesForType.find(
    (t: Template) => getTemplateId(t) === selectedTemplateId,
  );

  const handleDelete = async (template: Template) => {
    const templateId = getTemplateId(template);
    if (templateId) {
      await deleteTemplate(templateId);
    }
    // Since template is clicked to delete, the ID will be set as selected.
    // Just make sure we clear selections when deleting
    cancelEditCreate();
  };

  const handleEditingTemplateId = (templateId: number | null) => () => {
    if (templateId) {
      setIsEditingId(templateId);
      const template = templatesForType.find((t) => getTemplateId(t) === templateId);
      setTemplateString(template?.template || '');
    } else {
      cancelEditCreate();
    }
  };

  const handleSetTemplateString = (template: string) => setTemplateString(template);
  const toggleIsAdding = () => {
    if (isAddingNewTemplate) {
      cancelEditCreate();
    } else {
      setIsAddingNewTemplate(true);
    }
  };

  const handleInsert = (insertTemplateString: string) => () => {
    if (!insertTemplateString) {
      return;
    }

    ProductTelemetryController.logEvent('clicked use saved message template');

    let newTemplate = insertTemplateString;

    // There's not really a way to get messageType to be null here - it gets updated as soon as the editor is rendered
    // We store our templates with HTML intact, so if we go to use them for a text, we need to remove the <br>'s first,
    //    because otherwise we're going to double them when we inject <br>'s in the ui-rich-text-editor
    // This same thing is done in applicant-templates and will need to be updated if this is changed
    if (messageType && messageType === MESSAGE_TYPE.TEXT) {
      newTemplate = CkEditorNewlineTrimmer.trimBrs(newTemplate);
    }
    if (shouldParseTemplate) {
      insertTemplate(parser.parse(newTemplate));
    } else {
      insertTemplate(newTemplate);
    }

    onClose();
  };

  const handleChangePreventSaving = (disabled: boolean) => setPreventSaving(disabled);

  const updateMessage = async () => {
    const newTemplate = {
      ...editingTemplate,
      template: templateString,
    } as TextMessageTemplate;
    const templateId = getTemplateId(newTemplate);

    if (templateId) {
      await updateTemplate({ ...newTemplate, templateId });
    } else {
      await createTemplate({ ...newTemplate });
    }
  };

  const handleSave = async () => {
    await updateMessage();
    cancelEditCreate();
  };

  const cancelEditCreate = () => {
    setIsEditingId(null);
    setIsAddingNewTemplate(false);
    setTemplateString('');
    setSelectedTemplateId(null);
  };

  const handleSelect = (templateId: number | null) => {
    setSelectedTemplateId(templateId);
  };

  const handleSaveAndInsert = async () => {
    await updateMessage();
    handleInsert(templateString)();
  };

  return (
    <UiDialog title="Add Saved Message" size="sm" onClose={onClose}>
      <UiDialog.Content>
        {!isEditingId && !isAddingNewTemplate ? (
          <>
            <TemplateList
              templates={templatesForType}
              parser={parser}
              preventMessageParsing={!shouldParseTemplate}
              selectedTemplateId={selectedTemplateId}
              onEdit={handleEditingTemplateId}
              onSelect={handleSelect}
              onSave={handleSave}
              onDelete={handleDelete}
              recipientType="employee"
              templateType="message"
            />
            <div className="u-margin-y-md">
              <UiButton variant="tertiary" onClick={toggleIsAdding} size="sm">
                {templateType === 'SURVEY' ? 'New Saved Survey +' : 'New Saved Message +'}
              </UiButton>
            </div>
          </>
        ) : (
          <MessageTemplateEditor
            templateString={templateString}
            communicationTemplate={editingTemplate}
            parser={parser}
            handleChangePreventSaving={handleChangePreventSaving}
            handleSetTemplateString={handleSetTemplateString}
            recipientType="employee"
            templateType="message"
          />
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        {!isEditingId && !isAddingNewTemplate ? (
          <>
            <UiButton onClick={onClose} variant="tertiary">
              Cancel
            </UiButton>
            <UiButton
              disabled={!selectedTemplateId}
              onClick={handleInsert(selectedTemplate?.template || '')}
            >
              Use
            </UiButton>
          </>
        ) : null}
        {isEditingId || isAddingNewTemplate ? (
          <>
            <UiButton onClick={handleEditingTemplateId(null)} variant="tertiary">
              Cancel
            </UiButton>
            <div>
              <UiButton
                className="u-margin-right"
                onClick={handleSave}
                disabled={preventSaving || !templateString}
                variant="tertiary"
              >
                Save
              </UiButton>
              <UiButton onClick={handleSaveAndInsert} disabled={preventSaving || !templateString}>
                Save and Use
              </UiButton>
            </div>
          </>
        ) : null}
      </UiDialog.Actions>
    </UiDialog>
  );
};
